import { useEffect, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

// Step 3 — the centrepiece. A live multiplier counts up while the game's own
// object climbs: a football arcing at the goal (Defender), a struck ball flying
// off the bat (Howzzat), or a rocket blasting off (Crash / Rocket Boom).

const PEAK = 8.4
const RISE_MS = 3000
const HOLD_MS = 700 // pause on the peak before the loop restarts

const CAPTION = {
  football: 'Your striker’s on a run.',
  cricket: 'The runs are flowing.',
  rocket: 'Altitude climbing fast.',
}

// Count from 1.00 → PEAK on an ease-out curve, hold, then loop. Reduced motion
// skips the loop and just parks on the peak (derived at render, no setState).
const useClimbValue = (reduce) => {
  const [value, setValue] = useState(1)

  useEffect(() => {
    if (reduce) return undefined
    let raf
    let start
    const cycle = RISE_MS + HOLD_MS
    const tick = (time) => {
      if (start === undefined) start = time
      const elapsed = (time - start) % cycle
      const progress = Math.min(elapsed / RISE_MS, 1)
      const eased = 1 - (1 - progress) ** 2
      setValue(1 + (PEAK - 1) * eased)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [reduce])

  return reduce ? PEAK : value
}

const flightTransition = (reduce) =>
  reduce
    ? { duration: 0 }
    : { duration: RISE_MS / 1000, ease: 'easeOut', repeat: Infinity, repeatDelay: HOLD_MS / 1000 }

const FootballObject = ({ reduce }) => (
  <>
    {/* goal at the top-right corner */}
    <g transform="translate(150,8)" stroke="rgba(255,255,255,0.4)" fill="none">
      <rect x="0" y="0" width="42" height="28" strokeWidth="2" />
      <g stroke="rgba(255,255,255,0.2)" strokeWidth="1">
        <path d="M0,9 H42 M0,18 H42 M10.5,0 V28 M21,0 V28 M31.5,0 V28" />
      </g>
    </g>
    <motion.g
      animate={reduce ? { x: 168, y: 26, rotate: 0 } : { x: [20, 100, 168], y: [130, 66, 26], rotate: [0, 320, 660] }}
      transition={flightTransition(reduce)}
    >
      <circle r="9" fill="#ffffff" stroke="var(--htp-accent)" strokeWidth="1.5" />
      <path d="M0,-4.4 L4.2,-1.3 L2.6,3.6 L-2.6,3.6 L-4.2,-1.3 Z" fill="#0e151d" />
    </motion.g>
  </>
)

const CricketObject = ({ reduce }) => (
  <>
    {/* bat swinging from a fixed grip near the bottom-left */}
    <g transform="translate(34,132)">
      <motion.g
        animate={reduce ? { rotate: 4 } : { rotate: [-46, 6, -46] }}
        transition={flightTransition(reduce)}
      >
        <rect x="-2" y="-20" width="4" height="14" rx="2" fill="#9a6b3f" />
        <rect x="-5" y="-6" width="10" height="34" rx="4" fill="#d8b98c" stroke="var(--htp-accent)" strokeWidth="1.2" />
      </motion.g>
    </g>
    {/* the struck ball flying up toward the boundary */}
    <motion.g
      animate={reduce ? { x: 172, y: 24 } : { x: [44, 112, 172], y: [120, 58, 24] }}
      transition={flightTransition(reduce)}
    >
      <circle r="6" fill="#d6303a" />
      <path d="M-6,0 A6,6 0 0 1 6,0" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.8" />
    </motion.g>
  </>
)

const RocketObject = ({ reduce }) => (
  <motion.g
    animate={reduce ? { x: 150, y: 20 } : { x: [30, 92, 150], y: [128, 70, 20] }}
    transition={flightTransition(reduce)}
  >
    <motion.path
      d="M-3.4,7 L0,19 L3.4,7 Z"
      fill="var(--htp-accent)"
      animate={reduce ? undefined : { scaleY: [1, 1.7, 1], opacity: [0.65, 1, 0.65] }}
      transition={reduce ? undefined : { repeat: Infinity, duration: 0.35 }}
      style={{ transformOrigin: '0px 7px' }}
    />
    <path
      d="M0,-15 C5.5,-8 5.5,3 3,7 L-3,7 C-5.5,3 -5.5,-8 0,-15 Z"
      fill="#ffffff"
      stroke="var(--htp-accent)"
      strokeWidth="1.2"
    />
    <circle cy="-4" r="2.3" fill="var(--htp-accent)" />
    <path d="M-3,3 L-7.5,9.5 L-3,7 Z" fill="var(--htp-accent)" />
    <path d="M3,3 L7.5,9.5 L3,7 Z" fill="var(--htp-accent)" />
  </motion.g>
)

const OBJECTS = {
  football: FootballObject,
  cricket: CricketObject,
  rocket: RocketObject,
}

const ClimbVisual = ({ family }) => {
  const reduce = useReducedMotion()
  const value = useClimbValue(reduce)
  const GameObject = OBJECTS[family] ?? RocketObject

  return (
    <div className="htp-climb">
      <div className="htp-climb-mult">{value.toFixed(2)}×</div>
      <svg className="htp-climb-svg" viewBox="0 0 200 150" role="img" aria-label="Multiplier climbing">
        <path
          className="htp-climb-track"
          d="M20,134 Q96,96 182,18"
          fill="none"
          strokeDasharray="4 6"
        />
        <GameObject reduce={reduce} />
      </svg>
      <span className="htp-vlabel">{CAPTION[family] ?? CAPTION.rocket}</span>
    </div>
  )
}

export default ClimbVisual
